import axios from "axios";
import {globalStore} from "@pinia/global-store";

/**
 * Our API search call
 * @param {String} api The API url
 * @param {String} searchText The search text
 * @param {Object} params Any parameters to pass to the Rest API
 * @returns {Promise<AxiosResponse<any>|void|*[]>}
 */
export const searchCall = async (api, searchText = '', params = {}) => (await axios
    .post(api, {searchText: searchText, ...params})
    .then(({data}) => data.status === 200 && data.items)
    .catch(err => console.error(err)) || [])

/**
 * Makes a basic GET API call
 *
 * @param {String} api  The API url to request
 * @param {Object} params   The params to pass to the call
 * @returns {Promise<axios.AxiosResponse<any>|void>}
 */
export const apiFetch = async (api, params = {}) => (await axios
    .get(api, {params})
    .then((resp) => resp)
    .catch(err => console.error(err)))

/**
 * Makes a basic POST API call
 * @param {String} api  The API url to request
 * @param {Object} params   The params to pass to the call
 * @returns {Promise<axios.AxiosResponse<any>|void>}
 */
export const apiSend = async (api, params = {}) => (await axios
    .post(api, params)
    .then((resp) => resp)
    .catch(err => console.error(err)))

/**
 * Makes a basic POST API call but attaches a scroll handler
 *
 * @param {String} api          The API url to request
 * @param {Object} params       The params to pass to the call
 * @param {HTMLElement} goToElm The HTML element to scroll to
 * @returns {Promise<axios.AxiosResponse<any>|void>}
 */
export const paginationApiSend = async (api, params = {}, goToElm) => {
    goToElm && goToElm.scrollIntoView({behavior: 'smooth'});
    return await apiSend(api, params);
}

/**
 * Gets all the query Params
 * @returns {*} The query param we need to search for
 */
export const getQueryParams = (query) => {
    const returnQuery = {};
    const queryParams = new URLSearchParams(query);
    queryParams.forEach((value, key) => (returnQuery[key] = value));
    return returnQuery;
}

/**
 * Creates and sets the query parameters for the browser
 *
 * @param {Array|Object} params The parameters to convert to a query
 * @returns {{}}
 */
export const setQueryParams = (params) => {
    const data = {}
    if (typeof params === "object") {
        const keys = Object.keys(params);
        keys.forEach((key) => {
            if (typeof params[key] === "object") {
                Object
                    .keys(params[key])
                    .forEach(keyO => data[keyO] = params[key][keyO]);
            } else {
                // Check if the param is populated, otherwise remove it
                if (params[key]) data[key] = params[key];
                else delete data[key]
            }
        });
    }

    return data;
}

/**
 * Updates and sets the browser history to include the filtered options set by the user.
 * @param queryParams
 * @param dontUpdateHistory
 * @param windowUrl
 */
export const updateBrowserHistory = (
    queryParams,
    dontUpdateHistory = false,
    windowUrl = new URL(window.location)
) => {
    delete queryParams.ppp;
    delete queryParams.lat;
    delete queryParams.lng;

    if (!queryParams.lang || queryParams.lang !== 'en') {
        queryParams.lang = GLOBAL.langCode
    } else {
        delete queryParams.lang
    }

    // queryParams.lang === 'en' && (delete queryParams.lang)

    let searchParameters = new URLSearchParams();
    // Retain UTM parameters if they exist
    const utmParams = ['utm_source', 'utm_medium', 'utm_campaign','utm_term', 'utm_content'];
    utmParams.forEach(param => {
        if (windowUrl.searchParams.has(param)) {
            searchParameters.set(param, windowUrl.searchParams.get(param));
        }
    });

    for (const [key, value] of Object.entries(queryParams)) {
        if (key !== 'lat' && key !== 'lng' && value)
            searchParameters.set(key, value.toString());
    }

    const newUrl = `${windowUrl.origin + windowUrl.pathname}?${searchParameters.toString()}`;
    if (!dontUpdateHistory) {
        window.history.pushState(
            {path: newUrl},
            document.title,
            newUrl
        );
    }
}

/**
 * Handles the binding action of the search button in case
 * the selection of the item doesn't take the user there
 *
 * @param {Object} search The selected items object
 * @param {String} urlOverride URL Search Page override
 * @returns {*}
 */
export const setPredictiveResult = (search, urlOverride) => handlePredictiveUrl(search, urlOverride)


/**
 * Handles taking the user to the search page or to a trial
 * directly if the url contains a string value
 *
 * @param {String} name The Searched for value
 * @param {String} url The URL to the trial
 * @param {String} urlOverride the URL Override
 */
export const handlePredictiveUrl = ({name, url}, urlOverride = '') => {
    let searchUrl = urlOverride;

    if (!urlOverride) {
        searchUrl = globalStore()?.searchUrl;
    }

    // Retain any query parameters set by UTMs
    const queryParams = getQueryParams(new URL(window.location)?.search);

    // Add the search term even if its empty
    queryParams.search = name || ''
    delete queryParams.pages;
    delete queryParams.lang;

    // If the URL is empty, meaning searching for a word, go to the search page
    if (!url) setSearchPage(searchUrl, queryParams)
    else sendToPage(url)
}

/**
 * Used to build the search URL for the Prediction Dropdown search
 * and sets the "window?.location" URL with the queryParameters
 *
 * @param {String}  locationUrl  The sites search page new URL
 * @param {object}  query        An object of query params to pass
 *
 * @returns {string|*}  The "window?.location" URL
 */
export const setSearchPage = (locationUrl, query) => {
    const queryParams = new URLSearchParams(query);

    if (GLOBAL.langCode === 'en') {
        return (window.location = `${locationUrl}?${queryParams.toString()}`);
    }

    return (window.location = `${locationUrl}&${queryParams.toString()}`);
}

/**
 * Sends the user directly to a page
 *
 * @param {String} url The URL to send the user to
 */
export const sendToPage = (url) => window.location.assign(url)

export const updateLoading = (store, loading) => store.$patch({loading})
